import { Routes } from '@angular/router';
import { routerSegments } from './consts/routes';
import { LayoutComponent } from './components/layout/layout.component';
import { authenticatedGuard } from './guards/authenticated.guard';
import { permissionGuard } from './guards/permission.guard';
import { PermissionModels } from '@share-models';
import {
  PublicAccessDeniedPageComponent,
  PublicEmailVerificationPageComponent,
  PublicResetPasswordPageComponent,
} from '@auth';

export const routes: Routes = [
  {
    path: '',
    redirectTo: routerSegments.home,
    pathMatch: 'full',
  },
  {
    path: routerSegments.home,
    loadComponent: () => LayoutComponent,
    canActivate: [authenticatedGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/members/members.component').then(
            (m) => m.MembersComponent,
          ),
        canActivate: [permissionGuard],
        data: {
          breadcrumb: 'MEMBERS.TITLE',
          permissions: [
            PermissionModels.Permissions.PanicMembersManagement,
            PermissionModels.Permissions.PanicAppManagement,
          ],
        },
      },
    ],
  },
  {
    path: routerSegments.groups,
    loadComponent: () => LayoutComponent,
    canActivate: [authenticatedGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/groups/groups.component').then(
            (m) => m.GroupsComponent,
          ),
        canActivate: [permissionGuard],
        data: {
          breadcrumb: 'GROUPS.TITLE',
          permissions: [
            PermissionModels.Permissions.PanicGroupsManagement,
            PermissionModels.Permissions.PanicAppManagement,
          ],
        },
      },
      {
        path: ':groupId',
        loadComponent: () =>
          import('./pages/groups/group-detail/group-detail.component').then(
            (m) => m.GroupDetailComponent,
          ),
        canActivate: [permissionGuard],
        data: {
          breadcrumb: 'GROUPS.DETAIL.TITLE',
          permissions: [
            PermissionModels.Permissions.PanicGroupsManagement,
            PermissionModels.Permissions.PanicAppManagement,
          ],
        },
      },
    ],
  },
  {
    path: routerSegments.httpRequests,
    loadComponent: () => LayoutComponent,
    canActivate: [authenticatedGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/http-requests/http-requests.component').then(
            (m) => m.HttpRequestsComponent,
          ),
        canActivate: [permissionGuard],
        data: {
          breadcrumb: 'HTTP_REQUESTS.TITLE',
          permissions: [
            PermissionModels.Permissions.PanicHttpRequestManagement,
            PermissionModels.Permissions.PanicAppManagement,
          ],
        },
      },
    ],
  },
  {
    path: routerSegments.contactsActivityLog,
    loadComponent: () => LayoutComponent,
    canActivate: [authenticatedGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/contacts-activity-log/contacts-activity-log.component').then(
            (m) => m.ContactsActivityLogComponent,
          ),
        canActivate: [permissionGuard],
        data: {
          breadcrumb: 'ACTIVITY_LOG.TITLE',
          permissions: [
            PermissionModels.Permissions.PanicContactActivity,
            PermissionModels.Permissions.PanicAppManagement,
          ],
        },
      },
    ],
  },
  {
    path: routerSegments.userActivityLog,
    loadComponent: () => LayoutComponent,
    canActivate: [authenticatedGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/user-activity-log/user-activity-log.component').then(
            (m) => m.UserActivityLogComponent,
          ),
        canActivate: [permissionGuard],
        data: {
          breadcrumb: 'USER_ACTIVITY_LOG.TITLE',
          permissions: [
            PermissionModels.Permissions.PanicUserActivity,
            PermissionModels.Permissions.PanicAppManagement,
          ],
        },
      },
    ],
  },
  {
    path: routerSegments.manualEvents,
    loadComponent: () => LayoutComponent,
    canActivate: [authenticatedGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/manual-events/manual-events.component').then(
            (m) => m.ManualEventsComponent,
          ),
        canActivate: [permissionGuard],
        data: {
          breadcrumb: 'EVENT_TYPES.TITLE',
          permissions: [
            PermissionModels.Permissions.PanicManualEventsManagement,
            PermissionModels.Permissions.PanicAppManagement,
          ],
        },
      },
    ],
  },
  {
    path: routerSegments.polygons,
    loadComponent: () => LayoutComponent,
    canActivate: [authenticatedGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/polygons/polygons.component').then(
            (m) => m.PolygonsComponent,
          ),
        canActivate: [permissionGuard],
        data: {
          breadcrumb: 'POLYGONS.TITLE',
          permissions: [PermissionModels.Permissions.PanicAppManagement],
        },
      },
    ],
  },
  {
    path: routerSegments.accountDetails,
    loadComponent: () => LayoutComponent,
    canActivate: [authenticatedGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/account-details/account-details.component').then(
            (m) => m.AccountDetailsComponent,
          ),
      },
    ],
  },
  {
    path: routerSegments.systemConfiguration,
    loadComponent: () => LayoutComponent,
    canActivate: [authenticatedGuard],
    children: [
      {
        path: '',
        loadComponent: () =>
          import('./pages/system-configuration/system-configuration.component').then(
            (m) => m.SystemConfigurationComponent,
          ),
        canActivate: [permissionGuard],
        data: {
          breadcrumb: 'SYSTEM_CONFIGURATION.TITLE',
          permissions: [
            PermissionModels.Permissions.PanicSystemConfiguration,
            PermissionModels.Permissions.PanicAppManagement,
          ],
        },
      },
    ],
  },
  {
    path: routerSegments.logout,
    redirectTo: '',
    pathMatch: 'full',
  },
  {
    path: routerSegments.resetPassword,
    component: PublicResetPasswordPageComponent,
  },
  {
    path: routerSegments.emailVerification,
    component: PublicEmailVerificationPageComponent,
  },
  {
    path: routerSegments.accessDenied,
    component: PublicAccessDeniedPageComponent,
  },
  {
    path: routerSegments.notFound,
    loadChildren: () =>
      import('./pages/not-found/not-found.routes').then(
        (m) => m.notFoundRoutes,
      ),
  },
  {
    path: routerSegments.notPermitted,
    loadChildren: () =>
      import('./pages/not-permitted/not-permitted.routes').then(
        (m) => m.notPermittedRoutes,
      ),
  },
  {
    path: '**',
    redirectTo: routerSegments.home,
  },
];
